import type { TaskListQuery } from "@common/schemas/task.schema";
import type { Task } from "@common/types/task";
import type {
    ClientsWorkOverviewWorkRow,
    ClientsWorkOverviewWorkspaceRow,
} from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";

const KEY_SEPARATOR = "\n";

/** Joins ids into one string, so a memo keyed on it survives a new array with the same ids. */
export const toDemoKey = (values: ReadonlyArray<string | number>) =>
    values.join(KEY_SEPARATOR);

export const fromDemoKey = (key: string): string[] =>
    key === "" ? [] : key.split(KEY_SEPARATOR);

export interface DemoWorkFilters {
    search: string;
    clientIds: readonly string[];
    memberUserIds: readonly number[];
}

const includesText = (values: readonly string[], search: string) => {
    const needle = search.trim().toLowerCase();
    return (
        needle === "" ||
        values.some((value) => value.toLowerCase().includes(needle))
    );
};

/** Whether a fake application passes the page's search, client and person filters; others ignore fakes. */
export const isDemoWorkRowShown = ({
    row,
    filters,
}: {
    row: ClientsWorkOverviewWorkRow;
    filters: DemoWorkFilters;
}) =>
    includesText(
        [row.entityName, row.workspaceName, row.name],
        filters.search,
    ) &&
    (filters.clientIds.length === 0 ||
        filters.clientIds.includes(row.workspaceId)) &&
    (filters.memberUserIds.length === 0 ||
        [...row.preparers, ...row.reviewers, ...row.managers].some((member) =>
            filters.memberUserIds.includes(member.userId),
        ));

const isInFilter = <T>({
    values,
    value,
}: {
    values: readonly T[] | undefined;
    value: T | null;
}) =>
    values === undefined ||
    values.length === 0 ||
    (value !== null && values.includes(value));

export type DemoTaskQuery = Pick<
    TaskListQuery,
    | "clientIds"
    | "assigneeIds"
    | "isUnassigned"
    | "search"
    | "status"
    | "priorities"
>;

/** Whether a fake task passes the query's client, assignee, status, priority and search filters; others ignore fakes. */
export const isDemoTaskShown = ({
    task,
    query,
}: {
    task: Task;
    query: DemoTaskQuery;
}) => {
    const assigneeIds = query.assigneeIds ?? [];
    const isAssigneeFiltered =
        assigneeIds.length > 0 || query.isUnassigned === true;
    const isAssigneeShown =
        !isAssigneeFiltered ||
        (task.assigneeId === null
            ? query.isUnassigned === true
            : assigneeIds.includes(task.assigneeId));
    return (
        isAssigneeShown &&
        includesText([task.title], query.search ?? "") &&
        isInFilter({ values: query.clientIds, value: task.workspaceId }) &&
        isInFilter({ values: query.status, value: task.status }) &&
        isInFilter({ values: query.priorities, value: task.priority })
    );
};

/** Appends each fake application to its client's row; rows without fakes keep their reference. */
export const mergeDemoWorkRows = ({
    workspaceRows,
    demoRows,
}: {
    workspaceRows: ClientsWorkOverviewWorkspaceRow[];
    demoRows: readonly ClientsWorkOverviewWorkRow[];
}): ClientsWorkOverviewWorkspaceRow[] => {
    const hasMatch = workspaceRows.some((row) =>
        demoRows.some(({ workspaceId }) => workspaceId === row.workspaceId),
    );
    if (!hasMatch) {
        return workspaceRows;
    }
    return workspaceRows.map((row) => {
        const rowDemoRows = demoRows.filter(
            ({ workspaceId }) => workspaceId === row.workspaceId,
        );
        return rowDemoRows.length === 0
            ? row
            : { ...row, children: [...row.children, ...rowDemoRows] };
    });
};
